import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Employee } from '@/types';
import { useEmployeeSelectionContext } from '@/context/EmployeeSelectionContext';
import { EmployeeSelectionService } from '@/lib/employees/EmployeeSelectionService';

// Unified employee selection configuration
export interface EmployeeSelectionConfig {
  mode: 'single' | 'multiple';
  includeInactive?: boolean;
  includeUnassigned?: boolean;
  filterByRole?: string[];
  filterByContractStatus?: string[];
  sortBy?: 'name' | 'role' | 'created_at';
  sortOrder?: 'asc' | 'desc';
  searchFields?: ('name' | 'email' | 'role')[];
  requireShiftEligibility?: boolean;
  requirePayrollEligibility?: boolean;
}

// Employee selection data formats for different contexts
export interface BaseEmployeeSelection {
  id: string;
  name: string;
  email: string;
}

export interface StandardEmployeeSelection extends BaseEmployeeSelection {
  role_id?: string | null;
  roleInfo?: {
    name: string;
    default_pay_rate: number;
    pay_type: string;
  } | null;
  contract_type?: string;
  employment_unified_type?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  hourly_rate?: number;
  salary_adjustment?: number;
}

export interface CommunicationEmployeeSelection extends BaseEmployeeSelection {
  role?: string;
  user_id?: string;
  avatar_url?: string;
  is_online?: boolean;
  last_seen?: string;
}

export interface AdvancedEmployeeSelection extends StandardEmployeeSelection {
  weekly_hours?: number;
  rodne_cislo?: string;
  health_insurance_company?: string;
  iban?: string;
  salary_adjustment?: number;
}

// Selection state management
export interface EmployeeSelectionState {
  selectedEmployees: string[];
  searchTerm: string;
  availabilityFilters: {
    showOnlyAvailable?: boolean;
    checkTimeRange?: {
      start: Date;
      end: Date;
    };
  };
}

// Hook return type
export interface UseUnifiedEmployeeSelectionReturn<T extends BaseEmployeeSelection> {
  // Data
  employees: T[];
  filteredEmployees: T[];
  selectedEmployees: string[];
  
  // State
  loading: boolean;
  error: string | null;
  searchTerm: string;
  
  // Actions
  setSelectedEmployees: (employeeIds: string[]) => void;
  toggleEmployeeSelection: (employeeId: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  setSearchTerm: (term: string) => void;
  refetch: () => Promise<void>;
  
  // Utilities
  isEmployeeSelected: (employeeId: string) => boolean;
  getSelectedEmployeesData: () => T[];
  validateSelection: () => { isValid: boolean; errors: string[] };
}

// Default configuration
const DEFAULT_CONFIG: EmployeeSelectionConfig = {
  mode: 'single',
  includeInactive: false,
  includeUnassigned: true,
  sortBy: 'name',
  sortOrder: 'asc',
  searchFields: ['name', 'email'],
  requireShiftEligibility: false,
  requirePayrollEligibility: false,
};

/**
 * Unified employee selection hook that handles all employee selection patterns
 * across the application with configurable data formats and business logic
 */
export function useUnifiedEmployeeSelection<T extends BaseEmployeeSelection = StandardEmployeeSelection>(
  businessId: string | null,
  config: Partial<EmployeeSelectionConfig> = {},
  dataFormat: 'base' | 'standard' | 'communication' | 'advanced' = 'standard'
): UseUnifiedEmployeeSelectionReturn<T> {
  
  // Use employee selection context for caching and performance
  const context = useEmployeeSelectionContext();
  
  // Extract stable methods from context to prevent dependency issues
  const getCachedEmployees = useCallback((businessId: string) => {
    return context.getCachedEmployees(businessId);
  }, [context.getCachedEmployees]);
  
  const cacheEmployees = useCallback((businessId: string, employees: any[]) => {
    context.cacheEmployees(businessId, employees);
  }, [context.cacheEmployees]);

  // Merge configuration with defaults - serialize config for stable comparison
  const finalConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config,
  }), [JSON.stringify(config)]);

  // State management
  const [employees, setEmployees] = useState<T[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Build query based on data format requirements - dataFormat is stable so no need for useCallback
  const getSelectClause = () => {
    switch (dataFormat) {
      case 'base':
        return 'id, name, email';
      case 'communication':
        return `
          id,
          name,
          email,
          role_id,
          user_id,
          created_at,
          roles(name)
        `;
      case 'advanced':
        return `
          *,
          roleInfo:roles(id, name, default_pay_rate, pay_type, hours_per_month, business_id),
          children:employee_children(*),
          garnishments:employee_garnishments(*)
        `;
      case 'standard':
      default:
        return `
          id,
          name,
          email,
          role_id,
          contract_type,
          employment_unified_type,
          contract_start_date,
          contract_end_date,
          hourly_rate,
          salary_adjustment,
          roleInfo:roles(name, default_pay_rate, pay_type)
        `;
    }
  };

  // Fetch employees with unified business logic and caching
  const fetchEmployees = useCallback(async () => {
    if (!businessId) {
      setEmployees([]);
      setLoading(false);
      setError(null);
      return;
    }

    // Check cache first for standard format
    if (dataFormat === 'standard') {
      const cachedEmployees = getCachedEmployees(businessId);
      if (cachedEmployees) {
        // Apply filters using centralized service
        const filtered = EmployeeSelectionService.applyFilters(cachedEmployees, {
          includeInactive: finalConfig.includeInactive,
          requireShiftEligibility: finalConfig.requireShiftEligibility,
          requirePayrollEligibility: finalConfig.requirePayrollEligibility,
          filterByRole: finalConfig.filterByRole,
          filterByContractStatus: finalConfig.filterByContractStatus,
          sortBy: finalConfig.sortBy,
          sortOrder: finalConfig.sortOrder,
        });
        
        setEmployees(filtered as T[]);
        setLoading(false);
        setError(null);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Build the base query with the select clause
      let query = supabase
        .from('employees')
        .select(getSelectClause())
        .eq('business_id', businessId);

      // Apply filters based on configuration and available database fields
      if (finalConfig.filterByRole && finalConfig.filterByRole.length > 0) {
        query = query.in('role_id', finalConfig.filterByRole);
      }

      if (finalConfig.filterByContractStatus && finalConfig.filterByContractStatus.length > 0) {
        // Filter by contract_type since contract_status doesn't exist
        query = query.in('contract_type', finalConfig.filterByContractStatus);
      }

      // Note: includeInactive, requireShiftEligibility, and requirePayrollEligibility
      // are now handled in post-processing since the database fields don't exist

      // Apply sorting
      query = query.order(finalConfig.sortBy!, { ascending: finalConfig.sortOrder === 'asc' });

      const { data, error: queryError } = await query;

      if (queryError) {
        throw queryError;
      }

      // Transform data based on format requirements
      let transformedEmployees: T[];
      
      switch (dataFormat) {
        case 'communication':
          transformedEmployees = (data || []).map(emp => ({
            id: emp.id,
            name: emp.name,
            email: emp.email,
            role: emp.roles?.name || 'No Role',
            user_id: emp.user_id,
            avatar_url: undefined,
            is_online: false,
            last_seen: undefined,
          })) as T[];
          break;
        case 'advanced':
        case 'standard':
        case 'base':
        default:
          transformedEmployees = (data || []) as T[];
          break;
      }

      // Apply post-processing filters that can't be done at database level
      let finalEmployees = transformedEmployees;
      
      if (dataFormat === 'standard') {
        const standardEmployees = transformedEmployees as any[];
        
        // Apply business logic filters
        let filtered = standardEmployees;
        
        if (!finalConfig.includeInactive) {
          filtered = filtered.filter(emp => EmployeeSelectionService.isEmployeeActive(emp));
        }
        
        if (finalConfig.requireShiftEligibility) {
          filtered = filtered.filter(emp => EmployeeSelectionService.canEmployeeWorkShifts(emp));
        }
        
        if (finalConfig.requirePayrollEligibility) {
          filtered = filtered.filter(emp => EmployeeSelectionService.isEmployeeEligibleForPayroll(emp));
        }
        
        finalEmployees = filtered as T[];
        
        // Cache the original data (before filtering) for reuse
        if (data) {
          const standardEmployeesForCache = data.map(EmployeeSelectionService.transformToStandardFormat);
          cacheEmployees(businessId, standardEmployeesForCache);
        }
      }

      setEmployees(finalEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      toast.error('Failed to fetch employees', { 
        description: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  }, [businessId, finalConfig, getCachedEmployees, cacheEmployees]);

  // Filter employees based on search term using centralized service
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) {
      return employees;
    }

    // Use centralized filtering service for consistency
    return EmployeeSelectionService.filterEmployeesBySearch(
      employees as any[], // Type assertion needed for generic compatibility
      searchTerm,
      finalConfig.searchFields
    ) as T[];
  }, [employees, searchTerm, finalConfig.searchFields]);

  // Selection management actions
  const toggleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployees(prev => {
      if (finalConfig.mode === 'single') {
        return prev.includes(employeeId) ? [] : [employeeId];
      } else {
        return prev.includes(employeeId)
          ? prev.filter(id => id !== employeeId)
          : [...prev, employeeId];
      }
    });
  }, [finalConfig.mode]);

  const clearSelection = useCallback(() => {
    setSelectedEmployees([]);
  }, []);

  const selectAll = useCallback(() => {
    if (finalConfig.mode === 'multiple') {
      setSelectedEmployees(filteredEmployees.map(emp => emp.id));
    }
  }, [finalConfig.mode, filteredEmployees]);

  const isEmployeeSelected = useCallback((employeeId: string) => {
    return selectedEmployees.includes(employeeId);
  }, [selectedEmployees]);

  const getSelectedEmployeesData = useCallback(() => {
    return employees.filter(emp => selectedEmployees.includes(emp.id));
  }, [employees, selectedEmployees]);

  // Validation logic
  const validateSelection = useCallback(() => {
    const errors: string[] = [];
    
    if (finalConfig.mode === 'single' && selectedEmployees.length > 1) {
      errors.push('Only one employee can be selected');
    }
    
    if (selectedEmployees.length === 0) {
      errors.push('At least one employee must be selected');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [selectedEmployees, finalConfig.mode]);

  // Effects
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Reset selection when business changes
  useEffect(() => {
    setSelectedEmployees([]);
    setSearchTerm('');
    setError(null);
  }, [businessId]);

  return {
    // Data
    employees,
    filteredEmployees,
    selectedEmployees,
    
    // State
    loading,
    error,
    searchTerm,
    
    // Actions
    setSelectedEmployees,
    toggleEmployeeSelection,
    clearSelection,
    selectAll,
    setSearchTerm,
    refetch: fetchEmployees,
    
    // Utilities
    isEmployeeSelected,
    getSelectedEmployeesData,
    validateSelection,
  };
}

// Convenience hooks for common use cases
export const useEmployeeSelectionForShifts = (businessId: string | null) =>
  useUnifiedEmployeeSelection(businessId, {
    mode: 'single',
    requireShiftEligibility: true,
    includeInactive: false,
    sortBy: 'name',
  }, 'standard');

export const useEmployeeSelectionForCommunications = (businessId: string | null) =>
  useUnifiedEmployeeSelection<CommunicationEmployeeSelection>(businessId, {
    mode: 'multiple',
    includeInactive: false,
    sortBy: 'name',
  }, 'communication');

export const useEmployeeSelectionForEvents = (businessId: string | null) =>
  useUnifiedEmployeeSelection(businessId, {
    mode: 'multiple',
    includeInactive: false,
    sortBy: 'name',
  }, 'standard');

export const useEmployeeSelectionForPayroll = (businessId: string | null) =>
  useUnifiedEmployeeSelection<AdvancedEmployeeSelection>(businessId, {
    mode: 'multiple',
    requirePayrollEligibility: true,
    includeInactive: false,
    sortBy: 'name',
  }, 'advanced');